import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { FilialeTheme } from './FilialeTheme';
import { PDGViewBanner } from './PDGViewBanner';
import { LazyErrorBoundary } from './LazyErrorBoundary';
import { ToastHost } from './ToastHost';
import { FenetresHost } from '@/components/ui/Fenetres';
import { OfflineBanner } from '@/offline/OfflineBanner';
import { InstallPrompt } from '@/components/pwa/InstallPrompt';
import { useAuthStore } from '@/stores/authStore';

/**
 * Layout principal (CTO-FRONT-002).
 * Desktop : grid 2 colonnes sidebar 248px sticky + main scrollable.
 * Mobile (≤900px) : sidebar en drawer overlay, bouton hamburger fixed top-left,
 *   backdrop cliquable pour fermer, auto-close sur changement de route.
 */
export const AppLayout = () => {
  const pdgViewFiliale = useAuthStore((s) => s.pdgViewFiliale);
  const pdgViewWrite   = useAuthStore((s) => s.pdgViewWrite);
  const societes       = useAuthStore((s) => s.societes);
  const societeChoisie = useAuthStore((s) => s.societeChoisie);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  // Fermer le drawer sur changement de route (navigation)
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // Fermer le drawer sur touche Escape
  useEffect(() => {
    if (!mobileNavOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileNavOpen(false); };
    document.addEventListener('keydown', handler);
    // Empêche le scroll body pendant que le drawer est ouvert
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileNavOpen]);

  // Classes globales — mode PDG view. La classe pdg-view-mode grise et bloque
  // les boutons d'action (pointer-events:none). Elle n'est ajoutée que si
  // l'écriture n'est PAS activée : dès que le PDG bascule le toggle "Écriture",
  // on retire la classe pour rendre les boutons cliquables.
  useEffect(() => {
    if (pdgViewFiliale && !pdgViewWrite) document.body.classList.add('pdg-view-mode');
    else document.body.classList.remove('pdg-view-mode');
    return () => { document.body.classList.remove('pdg-view-mode'); };
  }, [pdgViewFiliale, pdgViewWrite]);

  // Lot 1, étape 1.7 (D1.7-a) : rattaché à plusieurs sociétés et pas encore choisi depuis la connexion → choix d'abord.
  if (societes && societes.filter((s) => s.ouvrable).length > 1 && !societeChoisie) {
    return <Navigate to="/choisir-societe" replace />;
  }

  return (
    <FilialeTheme>
      <div className={`ds-app${mobileNavOpen ? ' ds-mobile-nav-open' : ''}`}>
        {/* Bouton hamburger — visible uniquement en dessous de 900px via CSS */}
        <button
          type="button"
          className="ds-mobile-menu-btn"
          onClick={() => setMobileNavOpen((v) => !v)}
          aria-label={mobileNavOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          aria-expanded={mobileNavOpen}
        >
          {mobileNavOpen ? <X size={22} /> : <Menu size={22} />}
          <span className="ds-mobile-menu-btn-label">
            {mobileNavOpen ? 'Fermer' : 'Menu'}
          </span>
        </button>

        {/* Backdrop mobile — visible seulement quand drawer ouvert */}
        {mobileNavOpen && (
          <div
            className="ds-mobile-backdrop"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden
          />
        )}

        <Sidebar />
        <main className="ds-main">
          <PDGViewBanner />
          <OfflineBanner />
          {/* Global safety net : un crash sur une route ne doit jamais
              faire disparaître la sidebar (ticket SIMGROUP-2026-002 B-01) */}
          <LazyErrorBoundary>
            <Outlet />
          </LazyErrorBoundary>
        </main>
        <InstallPrompt />
        <ToastHost />
        <FenetresHost />
      </div>
    </FilialeTheme>
  );
};
